import {CommonModule} from '@angular/common';
import {Component, Inject} from '@angular/core';
import {TranslateModule} from '@ngx-translate/core';
import {BulkIdsAcceptedResponse} from '@gatewee/types/process-bulk-ids';
import {TuiButton, TuiDialogContext} from '@taiga-ui/core';
import {POLYMORPHEUS_CONTEXT} from '@taiga-ui/polymorpheus';

/** Polymorpheus dialog `data`: bulk acceptance payload from `POST …/update-bulk`. */
export type GateweeBulkOutcomeDialogData = BulkIdsAcceptedResponse;

@Component({
  selector: 'gatewee-bulk-outcome-dialog',
  standalone: true,
  imports: [CommonModule, TranslateModule, TuiButton],
  template: `
    <div class="gw-flex gw-max-h-[min(80vh,640px)] gw-flex-col gw-gap-4 gw-p-6">
      <div class="gw-shrink-0 gw-space-y-1">
        <h2 class="gw-text-lg gw-font-semibold gw-text-text-primary">
          {{ 'common.bulk.outcome.title' | translate }}
        </h2>
        <p class="gw-text-sm gw-text-text-secondary">
          {{
            'common.bulk.outcome.acceptedSummary'
              | translate : {count: data.accepted}
          }}
        </p>
      </div>

      <div
        *ngIf="data.ids.length === 0"
        class="gw-rounded-lg gw-border gw-border-[var(--tui-border-normal)] gw-px-4 gw-py-8 gw-text-center gw-text-sm gw-text-text-secondary"
      >
        {{ 'common.bulk.outcome.empty' | translate }}
      </div>

      <ng-container *ngIf="data.ids.length > 0">
        <div class="gw-flex gw-shrink-0 gw-flex-wrap gw-items-center gw-gap-2">
          <input
            type="search"
            class="gw-min-w-[12rem] gw-flex-1 gw-rounded-lg gw-border gw-border-[var(--tui-border-normal)] gw-bg-[var(--tui-background-base)] gw-px-3 gw-py-2 gw-text-sm gw-text-text-primary focus:gw-outline-none focus:gw-ring-2 focus:gw-ring-brand-primary"
            [placeholder]="'common.bulk.outcome.searchPlaceholder' | translate"
            [value]="search"
            (input)="onSearchInput($event)"
          />
          <button
            tuiButton
            appearance="secondary"
            size="s"
            type="button"
            (click)="copyIds()"
          >
            {{ 'common.bulk.outcome.copyIds' | translate }}
          </button>
        </div>

        <p class="gw-shrink-0 gw-text-xs gw-text-text-secondary">
          {{
            'common.bulk.outcome.shownCount'
              | translate : {shown: filteredIds.length, total: data.ids.length}
          }}
        </p>

        <div
          class="gw-min-h-0 gw-flex-1 gw-overflow-auto gw-rounded-lg gw-border gw-border-[var(--tui-border-normal)] gw-bg-[var(--tui-background-base)]"
        >
          <ul class="gw-m-0 gw-list-none gw-p-0">
            <li
              *ngFor="let id of filteredIds; trackBy: trackById"
              class="gw-bulk-outcome-id-row gw-border-b gw-border-[var(--tui-border-normal)] gw-px-3 gw-py-2 gw-font-mono gw-text-sm gw-text-text-primary last:gw-border-b-0"
            >
              {{ id }}
            </li>
          </ul>
          <p
            *ngIf="filteredIds.length === 0"
            class="gw-px-3 gw-py-8 gw-text-center gw-text-sm gw-text-text-secondary"
          >
            {{ 'common.bulk.outcome.noSearchResults' | translate }}
          </p>
        </div>
      </ng-container>

      <div class="gw-flex gw-shrink-0 gw-justify-end gw-pt-2">
        <button tuiButton appearance="primary" type="button" (click)="close()">
          {{ 'common.actions.done' | translate }}
        </button>
      </div>
    </div>
  `,
  styles: [
    `
      .gw-bulk-outcome-id-row {
        content-visibility: auto;
        contain-intrinsic-size: auto 2.5rem;
      }
    `,
  ],
})
export class GateweeBulkOutcomeDialogComponent {
  search = '';

  constructor(
    @Inject(POLYMORPHEUS_CONTEXT)
    private readonly context: TuiDialogContext<
      void,
      GateweeBulkOutcomeDialogData
    >,
  ) {}

  get data(): BulkIdsAcceptedResponse {
    return this.context.data;
  }

  get filteredIds(): number[] {
    const query = this.search.trim();
    if (!query) {
      return this.data.ids;
    }
    return this.data.ids.filter(id => String(id).includes(query));
  }

  trackById = (_: number, id: number) => id;

  onSearchInput(event: Event): void {
    this.search = (event.target as HTMLInputElement).value;
  }

  async copyIds(): Promise<void> {
    const text = this.data.ids.join('\n');
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return;
    }

    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    textarea.remove();
  }

  close(): void {
    this.context.completeWith();
  }
}
